export const SHARE_LABEL_MAX = 42;
const COUNTY_SHARE_THRESHOLD = 0.6;
const COUNTY_SUFFIX = /\b(county|parish|borough|census area|municipality)$/i;

const clean = (v) => (v == null ? "" : String(v).trim());
const num = (v) =>
  v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v);
const titleCase = (v) =>
  clean(v)
    .toLowerCase()
    .replace(/(^|[\s-])([a-z])/g, (m, sep, ch) => sep + ch.toUpperCase());
const stateCode = (v) =>
  clean(v).length === 2 ? clean(v).toUpperCase() : clean(v);
const countyName = (v) => {
  const c = titleCase(v);
  return !c || COUNTY_SUFFIX.test(c) ? c : `${c} County`;
};
const place = (city, state) => (state ? `${city}, ${state}` : city);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const joinAnd = (items) =>
  items.length <= 2
    ? items.join(" and ")
    : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

const pickShort = (candidates) => {
  const list = candidates.filter(Boolean);
  const fit = list.find((c) => c.length <= SHARE_LABEL_MAX);
  if (fit) return fit;
  const last = list[list.length - 1] || "";
  return `${last.slice(0, SHARE_LABEL_MAX - 1).trim()}…`;
};

const normalizeZips = (zips) =>
  (Array.isArray(zips) ? zips : [])
    .map((z) => (typeof z === "object" && z !== null ? z : { zipCode: z }))
    .map((z) => ({
      zipCode: clean(z.zipCode ?? z.ZipCode),
      city: titleCase(z.city ?? z.City),
      county: clean(z.county ?? z.County),
      state: stateCode(z.state ?? z.State),
    }))
    .filter(
      (z, i, all) =>
        z.zipCode && all.findIndex((o) => o.zipCode === z.zipCode) === i,
    )
    .sort((a, b) => a.zipCode.localeCompare(b.zipCode));

const rankBy = (zips, key) => {
  const counts = new Map();
  zips.forEach((z) => {
    if (!z[key]) return;
    const id = `${z[key]}|${z.state}`;
    const entry = counts.get(id) || {
      value: z[key],
      count: 0,
      state: z.state,
    };
    entry.count += 1;
    counts.set(id, entry);
  });
  return [...counts.values()].sort(
    (a, b) => b.count - a.count || a.value.localeCompare(b.value),
  );
};

const onlyState = (zips) => {
  const states = [...new Set(zips.map((z) => z.state).filter(Boolean))];
  return states.length === 1 ? states[0] : null;
};

const area = (kind, preposition, candidates) => ({
  kind,
  preposition,
  candidates,
});

const describeZips = (zips) => {
  const total = zips.length;
  const state = onlyState(zips);
  const cities = rankBy(zips, "city");
  const lead = cities[0];
  const leadPlace = lead && place(lead.value, state || lead.state);
  const others = cities.length - 1;
  const more = plural(others, "more city", "more cities");

  if (total === 1) {
    const [z] = zips;
    const label = z.city
      ? `ZIP ${z.zipCode} · ${place(z.city, z.state)}`
      : `ZIP ${z.zipCode}`;
    return area("zip", "in", [label, `ZIP ${z.zipCode}`]);
  }
  if (total <= 3) {
    if (!lead || zips.some((z) => !z.city)) {
      return area("zips", "in", [
        `ZIP codes ${joinAnd(zips.map((z) => z.zipCode))}`,
        `${total} ZIPs`,
      ]);
    }
    if (!others && lead.count === total) {
      return area("city", "in", [
        `${leadPlace} (${zips.map((z) => z.zipCode).join(", ")})`,
        leadPlace,
      ]);
    }
    const names = cities.map((c) =>
      state ? c.value : place(c.value, c.state),
    );
    const label = state ? `${joinAnd(names)}, ${state}` : joinAnd(names);
    return area("cities", "in", [label, `${leadPlace} and ${more}`, leadPlace]);
  }
  const county = rankBy(zips, "county")[0];
  const countyInState =
    county && (state ? county.state === state : !county.state);
  if (countyInState && county.count / total >= COUNTY_SHARE_THRESHOLD) {
    const where = place(countyName(county.value), state);
    return area("county", "across", [
      `${total} ZIP codes in and around ${where}`,
      `${total} ZIPs in and around ${where}`,
      `${total} ZIPs around ${where}`,
      where,
    ]);
  }
  if (!lead) return area("spread", "in", [`${total} ZIP codes`]);
  if (!others) {
    return area("spread", "in", [
      `${total} ZIP codes in ${leadPlace}`,
      `${total} ZIPs in ${leadPlace}`,
      leadPlace,
    ]);
  }
  return area("spread", "in", [
    `${total} ZIP codes across ${lead.value} and ${more}`,
    `${total} ZIPs across ${lead.value} and ${more}`,
    `${lead.value} and ${more}`,
  ]);
};

const centerPlace = (ad, zips) => {
  if (clean(ad.city)) return place(titleCase(ad.city), stateCode(ad.state));
  const parts = clean(ad.location)
    .replace(/, USA$/i, "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length >= 2) {
    const st = parts[parts.length - 1].replace(/\s+\d{5}.*$/, "");
    return place(titleCase(parts[parts.length - 2]), stateCode(st));
  }
  const lead = rankBy(zips, "city")[0];
  return lead ? place(lead.value, onlyState(zips) || lead.state) : "";
};

const describeLocal = (ad) => {
  const zips = normalizeZips(ad.zipCode);
  const radius = num(ad.mapRadius ?? ad.Radius);
  const drawn = ad.drawnPolygon ?? ad.DrawnPolygonText;
  const hasDrawn = Array.isArray(drawn) ? drawn.length > 0 : Boolean(drawn);
  if (hasDrawn) {
    const where = centerPlace(ad, zips);
    return area("drawn", "in a", [
      where ? `Custom area in ${where}` : "Custom area",
      "Custom area",
    ]);
  }
  const zipMode =
    ad.isZipCodeBased == null
      ? zips.length > 0
      : Boolean(Number(ad.isZipCodeBased));
  if (!zipMode && radius > 0) {
    const where = centerPlace(ad, zips);
    const miles = Math.round(radius * 10) / 10;
    if (where) {
      return area("radius", "", [
        `Within ${miles} mi of ${where}`,
        `Within ${miles} mi of ${where.split(",")[0]}`,
      ]);
    }
  }
  if (zips.length) return describeZips(zips);
  if (clean(ad.city)) return area("city", "in", [centerPlace(ad, zips)]);
  return null;
};

const toPhrase = (preposition, label) => {
  const body = /^(Within|Custom)\b/.test(label)
    ? label[0].toLowerCase() + label.slice(1)
    : label;
  return preposition ? `${preposition} ${body}` : body;
};

export const describeShareLocation = (adDetails = {}) => {
  const server = adDetails?.shareArea;
  if (server?.shortLabel) {
    return {
      kind: server.kind,
      shortLabel: server.shortLabel,
      longLabel: server.longLabel || server.shortLabel,
      shortPhrase: server.shortPhrase || server.shortLabel,
      longPhrase: server.longPhrase || server.shortPhrase || server.shortLabel,
    };
  }
  const found = describeLocal(adDetails || {});
  if (!found) return null;
  const shortLabel = pickShort(found.candidates);
  const longLabel = found.candidates[0];
  return {
    kind: found.kind,
    shortLabel,
    longLabel,
    shortPhrase: toPhrase(found.preposition, shortLabel),
    longPhrase: toPhrase(found.preposition, longLabel),
  };
};

export const shareLocationPhrase = (adDetails) =>
  describeShareLocation(adDetails)?.shortPhrase || "";

export const shareLocationLongPhrase = (adDetails) =>
  describeShareLocation(adDetails)?.longPhrase || "";
